"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ShieldCheck, ArrowRight, FileText, CircleCheck, SlidersHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  categoryBadge: string;
  standardBadge: string;
  heading: string;
  lead: string;
  secondaryCta: string;
  imageAlt: string;
  chipText: string;
  cardNote: string;
  cardLink: string;
  specApertureLabel: string;
  specApertureVal: string;
  specWeaveLabel: string;
  specWeaveVal: string;
  specAlloyLabel: string;
  specAlloyVal: string;
  specStandardLabel: string;
  specStandardVal: string;
}
const COPY: Record<Lang, HeroCopy> = {
  en: {
    categoryBadge: "Technical Selection Hub",
    standardBadge: "ISO 9044 / ASTM E2016",
    heading: "Find the right mesh specification for your process.",
    lead: "Compare orthogonal wire mesh weaves, micron ratings, aperture tolerances, and corrosion-resistant alloys. Structured engineering guidance to match your target retention efficiency and operating pressure.",
    secondaryCta: "Filter Mesh Selection Guide",
    imageAlt: "Engineering comparison of precision stainless steel woven wire mesh apertures",
    chipText: "Woven & Sintered Media",
    cardNote: "Square, Dutch & Sintered specifications",
    cardLink: "Selection Guides",
    specApertureLabel: "Aperture Range",
    specApertureVal: "1 µm – 25 mm",
    specWeaveLabel: "Mesh & Weave",
    specWeaveVal: "1–635 Mesh / Dutch Weave",
    specAlloyLabel: "Primary Alloys",
    specAlloyVal: "AISI 304L / 316L",
    specStandardLabel: "Manufacturing Standard",
    specStandardVal: "ISO 9044 / ASTM E2016"
  },
  zh: {
    categoryBadge: "技术选型中心",
    standardBadge: "ISO 9044 / ASTM E2016",
    heading: "为您的工艺流程精准匹配丝网规格。",
    lead: "对比正交方孔丝网、荷兰密纹编织与烧结复合网，综合评估微米截留精度、有效开孔率与耐蚀合金牌号。工程化选型体系协助您平衡压差与分离效率。",
    secondaryCta: "过滤丝网选型指南",
    imageAlt: "高精度不锈钢编织丝网与过滤微孔结构比对",
    chipText: "编织与烧结过滤介质",
    cardNote: "方孔网、密纹网与烧结复合板技术参数",
    cardLink: "选型指南",
    specApertureLabel: "孔径截留范围",
    specApertureVal: "1 µm – 25 mm",
    specWeaveLabel: "目数与编织结构",
    specWeaveVal: "1–635 目 / 密纹编织",
    specAlloyLabel: "常用不锈钢材质",
    specAlloyVal: "AISI 304L / 316L",
    specStandardLabel: "制造执行标准",
    specStandardVal: "ISO 9044 / ASTM E2016"
  },
  es: {
    categoryBadge: "Centro de selección técnica",
    standardBadge: "ISO 9044 / ASTM E2016",
    heading: "Encuentre la especificación de malla adecuada para su proceso.",
    lead: "Compare tejidos de malla metálica ortogonal, clasificaciones de micras, tolerancias de abertura y aleaciones resistentes a la corrosión. Guía de ingeniería estructurada para optimizar retención y presión.",
    secondaryCta: "Guía de selección de mallas",
    imageAlt: "Comparación de ingeniería de aberturas de malla de alambre tejido de acero inoxidable",
    chipText: "Medios tejidos y sinterizados",
    cardNote: "Especificaciones de malla cuadrada, Dutch y sinterizada",
    cardLink: "Guías de selección",
    specApertureLabel: "Rango de abertura",
    specApertureVal: "1 µm – 25 mm",
    specWeaveLabel: "Malla y tejido",
    specWeaveVal: "1–635 Mesh / Tejido Dutch",
    specAlloyLabel: "Aleaciones principales",
    specAlloyVal: "AISI 304L / 316L",
    specStandardLabel: "Norma de fabricación",
    specStandardVal: "ISO 9044 / ASTM E2016"
  },
  de: {
    categoryBadge: "Technisches Auswahlzentrum",
    standardBadge: "ISO 9044 / ASTM E2016",
    heading: "Finden Sie die passende Maschenspezifikation für Ihren Prozess.",
    lead: "Vergleichen Sie orthogonale Drahtgewebe, Mikron-Abscheidegrade, Öffnungstoleranzen und korrosionsbeständige Legierungen. Strukturierte technische Beratung für Trennschärfe und Betriebsdruck.",
    secondaryCta: "Leitfaden Drahtgewebeauswahl",
    imageAlt: "Ingenieurtechnischer Vergleich von Maschenweiten bei Edelstahl-Drahtgeweben",
    chipText: "Gewebe- und Sintermedien",
    cardNote: "Spezifikationen für Quadrat-, Tressen- und Sintergewebe",
    cardLink: "Auswahlleitfäden",
    specApertureLabel: "Maschenweitenbereich",
    specApertureVal: "1 µm – 25 mm",
    specWeaveLabel: "Maschenzahl & Bindung",
    specWeaveVal: "1–635 Mesh / Tressengewebe",
    specAlloyLabel: "Hauptlegierungen",
    specAlloyVal: "AISI 304L / 316L",
    specStandardLabel: "Fertigungsnorm",
    specStandardVal: "ISO 9044 / ASTM E2016"
  },
  fr: {
    categoryBadge: "Centre de sélection technique",
    standardBadge: "ISO 9044 / ASTM E2016",
    heading: "Trouvez la spécification de toile métallique adaptée à votre procédé.",
    lead: "Comparez les armures de toiles métalliques orthogonales, les seuils en microns, les tolérances d'ouverture et les alliages anticorrosion. Conseil technique structuré pour allier rétention et débit.",
    secondaryCta: "Guide de sélection des toiles",
    imageAlt: "Comparaison technique des ouvertures de toiles métalliques tissées en acier inoxydable",
    chipText: "Médias tissés et frittés",
    cardNote: "Spécifications toiles carrées, toiles reps et laminés frittés",
    cardLink: "Guides de sélection",
    specApertureLabel: "Plage d'ouverture",
    specApertureVal: "1 µm – 25 mm",
    specWeaveLabel: "Maillage et tissage",
    specWeaveVal: "1–635 Mesh / Toile reps",
    specAlloyLabel: "Alliages principaux",
    specAlloyVal: "AISI 304L / 316L",
    specStandardLabel: "Norme de fabrication",
    specStandardVal: "ISO 9044 / ASTM E2016"
  },
  pt: {
    categoryBadge: "Centro de seleção técnica",
    standardBadge: "ISO 9044 / ASTM E2016",
    heading: "Encontre a especificação de malha correta para o seu processo.",
    lead: "Compare tecelagens de malhas metálicas ortogonais, graus de retenção em mícrons, tolerâncias de abertura e ligas resistentes à corrosão. Orientação estruturada para atender às suas metas operacionais.",
    secondaryCta: "Guia de seleção de malhas",
    imageAlt: "Comparação de engenharia de aberturas de malha de arame tecido em aço inoxidável",
    chipText: "Meios tecidos e sinterizados",
    cardNote: "Especificações de malha quadrada, Dutch e sinterizada",
    cardLink: "Guias de seleção",
    specApertureLabel: "Faixa de abertura",
    specApertureVal: "1 µm – 25 mm",
    specWeaveLabel: "Malha e tecelagem",
    specWeaveVal: "1–635 Mesh / Trama Dutch",
    specAlloyLabel: "Ligas principais",
    specAlloyVal: "AISI 304L / 316L",
    specStandardLabel: "Norma de fabricação",
    specStandardVal: "ISO 9044 / ASTM E2016"
  }
};
export default function SelectionHero() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, COPY);
  const technicalHighlights = [{
    label: c.specApertureLabel,
    val: c.specApertureVal
  }, {
    label: c.specWeaveLabel,
    val: c.specWeaveVal
  }, {
    label: c.specAlloyLabel,
    val: c.specAlloyVal
  }, {
    label: c.specStandardLabel,
    val: c.specStandardVal
  }];
  return <section id="selectionguidehero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Technical Badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{c.categoryBadge}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>{c.standardBadge}</span>
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {c.heading}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {c.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/FilterMeshSelectionGuidePage" className="inline-flex items-center gap-2">
                  <FileText className="size-4 text-primary" aria-hidden="true" />
                  <span>{c.secondaryCta}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Card with Page Image (5 Columns on lg) */}
          <Reveal delay={100} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="mf-card-media mf-ratio-hero w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-a-buyer-s-hands-comp-d976c6.png" alt={c.imageAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{c.chipText}</span>
                </div>
              </div>

              {/* Single Footer Line */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{c.cardNote}</span>
                  </div>
                  <Link to="/FilterMeshSelectionGuidePage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{c.cardLink}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {technicalHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.val}</span>
            </div>)}
        </div>
      </div>
    </section>;
}